import Map from "./Map.js";
import { SEMANTICS_OVERLAYS } from "../../utils/semantics.js";
import { SEMANTICS_OVERLAY_MAXZOOM, SEMANTICS_OVERLAY_MINZOOM } from "../../utils/SemanticsMapProtocol.js";


/**
 * MapMore is a more complete version of [Map UI component](#Panoramax.components.ui.Map).
 *
 * It offers advanced features filters and semantic overlays.
 *
 * Note that all functions of [MapLibre GL JS class Map](https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/) are also available.
 *
 * ℹ️ Many style management is done in [MapStyleComposer](#Panoramax.utils.MapStyleComposer), which is available as a property of parent component.
 *
 * ⚠️ This class doesn't inherit from [EventTarget](https://developer.mozilla.org/fr/docs/Web/API/EventTarget), so it doesn't have `addEventListener` and `dispatchEvent` functions.
 * It uses instead [`on`](https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/#on) and `fire` functions from MapLibre Map class.
 * `fire` function doesn't take directly [`Event`](https://developer.mozilla.org/fr/docs/Web/API/Event) objects, but a string and object data.
 * @class Panoramax.components.ui.MapMore
 * @extends Panoramax.components.ui.Map
 * @param {Panoramax.components.core.Basic} parent The parent view
 * @param {Element} container The DOM element to create into
 * @param {object} [options] The map options (any of [MapLibre GL settings](https://maplibre.org/maplibre-gl-js/docs/API/type-aliases/MapOptions/) or any supplementary option defined here)
 * @param {object} [options.raster] The MapLibre raster source for aerial background. This must be a JSON object following [MapLibre raster source definition](https://maplibre.org/maplibre-style-spec/sources/#raster).
 * @param {object} [options.basemaps] List of complementary basemaps, as { id: {MapLibre Style JSON or URL} }. Note that special IDs streets and aerial are reserved for default backgrounds.
 * @param {string} [options.background=streets] Choose default map background to display (streets or aerial, if raster aerial background available). Defaults to streets.
 * @param {string} [options.attributionControl.customAttribution] To override default map attribution.
 * @param {boolean} [options.picMarkerDraggable] To make the picture marker draggable, default to false.
 * @param {object} [options.indoor] The indoor= MapLibre plugin options. This must be a JSON object following [IndoorEqual parameters](https://indoorequal.com/doc/maplibre-gl-indoorequal/api#parameters). Note that this is only available if [maplibre-gl-indoorequal](https://indoorequal.com/doc/maplibre-gl-indoorequal) plugin is loaded in your web page.
 * @param {string} [options.indoor.level] (only if indoor= plugin is enabled) The initial indoor level to display. Defaults to ground or disabled depending on indoor map availability.
 * @param {boolean} [options.indoor.visible=true] (only if indoor= plugin is enabled) The initial indoor visibility. Set to false to avoid data loading before user explicitly enables indoor through widgets. If set to false, you may not set any level option in component.
 * @param {string} [options.theme=default] The map theme (default, age, score, type)
 * @fires Panoramax.components.ui.Map#sequence-hover
 * @fires Panoramax.components.ui.Map#sequence-click
 * @fires Panoramax.components.ui.Map#picture-click
 * @example
 * const map = new Panoramax.components.ui.MapMore(viewer, mapNode, {center: {lat: 48.7, lng: -1.7}});
 */
export default class MapMore extends Map {
	constructor(parent, container, options = {}) {
		super(parent, container, options);

		// Map theme
		if(this._options.theme) { this._parent.mapStyleComposer.setPanoramaxTheme(this._options.theme); }

		// Features restrictions
		this._featuresRestrict = { pictures: {}, sequences: {} };
	}

	/** @private */
	async _postLoad() {
		if(this._parent.api._endpoints.queryables) {
			SEMANTICS_OVERLAYS.forEach(so => this.addSemanticOverlay(...so));
		}
		await super._postLoad();
	}

	/**
	 * Change the map filters
	 * @param {object} filters Filtering values
	 * @param {string} [filters.minDate] Start date for pictures (format YYYY-MM-DD)
	 * @param {string} [filters.maxDate] End date for pictures (format YYYY-MM-DD)
	 * @param {string} [filters.pic_type] Type of picture to keep (flat, equirectangular)
	 * @param {string} [filters.camera] Camera make and model to keep
	 * @param {string} [filters.theme] Map theme to use
	 * @param {number[]} [filters.qualityscore] QualityScore values, as a list of 1 to 5 grades
	 * @param {number[]} [filters.gps] GPS precision grades, as a list of 1 to 5 grades
	 * @param {string} [filters.source] Source of vector tiles, either "local" (this API) or "metacatalog" (federated catalog)
	 * @memberof Panoramax.components.core.MapMore#
	 */
	setFilters(filters) {
		const prevFilters = this._parent.mapStyleComposer.panoramaxFilters;
		const nextFilters = Object.assign({featuresRestrictions: prevFilters.featuresRestrictions}, filters);
		this._parent.mapStyleComposer.setPanoramaxFilters(nextFilters);

		if(filters.theme) { this._parent.mapStyleComposer.setPanoramaxTheme(filters.theme); }
	}

	/**
	 * Restrict the visible pictures or sequences on map.
	 * @param {string} restriction "keep" or "hide"
	 * @param {string} type "pictures" or "sequences"
	 * @param {string[]} features The list of features UUIDs concerned by this rule
	 * @memberof Panoramax.components.core.MapMore#
	 */
	restrictVisibleFeatures(restriction, type, features) {
		this._featuresRestrict[type] = { rule: restriction, features };

		const prevFilters = this._parent.mapStyleComposer.panoramaxFilters;
		const nextFilters = Object.assign({}, prevFilters, {featuresRestrictions: this._featuresRestrict});
		this._parent.mapStyleComposer.setPanoramaxFilters(nextFilters);
	}

	/**
	 * Add a new semantic overlay on map
	 * @param {string} id The layer ID
	 * @param {string} apiFilter The API filter for semantics, [as documented in API](https://api.panoramax.xyz/redoc#tag/Items/operation/get_api-search-5769f3316ef291861415451c98c2e33f)
	 * @param {object} layerStyle The [MapLibre GL layer style](https://maplibre.org/maplibre-style-spec/layers/) to apply, including default visibility. Note that a "metadata" property is required to properly show it in Tags widget. If no metadata is set, layer is added as a "secret" layer (for internal mechanics).
	 * @param {object} [sprites] The optional sprites to load
	 * @memberof Panoramax.components.core.MapMore#
	 */
	addSemanticOverlay(id, apiFilter, layerStyle, sprites = {}) {
		if(!this._parent.getAPI()._endpoints.queryables) {
			throw new Error("Semantics overlay are not available on API");
		}

		const srcId = "sem-"+id;
		const isVisible = layerStyle.layout.visibility !== "none";
		const myLayerStyle = {
			sources: {
				[srcId]: {
					type: "vector",
					tiles: [`sem:///${apiFilter}/{z}/{x}/{y}`],
					minzoom: SEMANTICS_OVERLAY_MINZOOM,
					maxzoom: SEMANTICS_OVERLAY_MAXZOOM
				}
			},
			layers: [
				{
					id,
					source: srcId,
					"source-layer": "layer",
					...JSON.parse(JSON.stringify(layerStyle))
				}
			],
			sprite: Object
				.entries(sprites || {})
				.map(([k,v]) => ({ id: k, url: v }))
		};
		delete myLayerStyle.layers[0].layout.visibility; // Get rid of original visibility
		this._parent.mapStyleComposer.addDataOverlay(
			srcId,
			myLayerStyle,
			isVisible
		);

		// Click event
		this.on("click", id, e => {
			e.preventDefault();
			if(e?.features?.length > 0) {
				const f = e.features[0];
				this._parent.select(f.properties.seqid, f.properties.picid);
				this._parent.psv.addEventListener("picture-loaded", () => {
					const picMeta = this._parent.psv.getPictureMetadata();
					if(picMeta.properties?.annotations?.length === 1) {
						this._parent.psv.focusOnAnnotation(picMeta.properties.annotations[0].id);
					}
				}, { once: true });
			}
		});
	}

	/**
	 * List existing semantic overlays, either visible or not.
	 * @memberof Panoramax.components.core.MapMore#
	 * @returns {object[]} List of MapLibre layers
	 */
	getSemanticOverlays() {
		return Object
			.entries(this._parent.mapStyleComposer.layerRanges.dataOverlays)
			.filter(([k,]) => k.startsWith("sem-"))
			.map(([,v]) => v.layers[0]);
	}

	/**
	 * Change visibility of all semantics overlays at once.
	 * @param {boolean} visible True to make all visible
	 * @memberof Panoramax.components.core.MapMore#
	 */
	toggleAllSemanticOverlays(visible) {
		Object
			.keys(this._parent.mapStyleComposer.layerRanges.dataOverlays)
			.filter(k => k.startsWith("sem-"))
			.forEach(k => this._parent.mapStyleComposer.switchDataOverlayVisibility(k, visible));
	}
}
